import { NiceChannelBox } from '@/features/analytics/components/nice-channel-box'
import {
    useInviteLinkSelection,
    useSearch,
    useSortedData,
} from '@/features/analytics/hooks'
import {
    AnalyticsTableRowTypeChannels,
    transformLinksToTableFormatChannels,
} from '@/features/analytics/types'
import { MagnifyingGlass, NewIcon } from '@/shared/assets'
import { SortHeader, StaticHeader, Table } from '@/shared/components'
import {
    Checkbox,
    CopyLink,
    GreyRubleIconInline,
    RubleIconInline,
} from '@/shared/ui'
import { Channel } from '@/true-fsd/data/advertiser-account'
import { useChannelLinksByGroup } from '@/true-fsd/data/link'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import clsx from 'clsx'
import { useMemo } from 'react'
import './retentionTable.scss'
import { useGroupNameChannels } from '@/widgets/analytics/invite-links/hooks/useGroupName'

interface Props {
    groupId: number
    activeChannel: Channel
}

export const RetentionTable = ({ groupId, activeChannel }: Props) => {
    const groupName = useGroupNameChannels({
        channelId: activeChannel.channel_id,
    })

    const { data: inviteLinks = [] } = useChannelLinksByGroup({
        channel_id: activeChannel.channel_id,
        channel_links_group_id: groupId,
        filter: groupName !== 'Все ссылки',
    })

    const formattedRows = useMemo(() => {
        if (!inviteLinks) return []
        return transformLinksToTableFormatChannels(inviteLinks)
    }, [inviteLinks])

    const { selectedLinksSet, onLinkSelect } = useInviteLinkSelection({
        groupId,
    })

    const { searchQuery, setSearchQuery, filteredData } =
        useSearch(formattedRows)
    const { sortedData, sortConfig, handleSort } = useSortedData(filteredData)

    const tableHead = (
        <tr>
            <th>{/* <div className='border-box'></div> */}</th>
            <th>
                <div className="search-header">
                    <div className="search-input-wrapper">
                        <img
                            src={MagnifyingGlass}
                            alt="Search"
                            className="search-icon"
                        />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Название"
                            className="search-input"
                        />
                    </div>
                    <SortHeader<AnalyticsTableRowTypeChannels>
                        title=""
                        field="name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                </div>
            </th>
            <th>
                <StaticHeader
                    title="Ссылка"
                    spanClassName="static-header__link"
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title={
                        <>
                            Бюджет <GreyRubleIconInline />
                        </>
                    }
                    field="budget"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Дата создания"
                    field="creationDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Кол-во подписчиков"
                    field="subscribers"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Охват"
                    field="reach"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Вступлений"
                    field="subscribed"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="CR %"
                    field="CTR"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title={
                        <>
                            CPM <GreyRubleIconInline />
                        </>
                    }
                    field="CPM"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title={
                        <>
                            CPS <GreyRubleIconInline />
                        </>
                    }
                    field="CPS"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Отписки"
                    field="unsubscribed"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Отписки, %"
                    field="unsubscriptionPercentage"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title="Итого вступлений"
                    field="totalSubscribed"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title={
                        <>
                            Итого CPS <GreyRubleIconInline />
                        </>
                    }
                    field="total_CPS"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
        </tr>
    )

    const tableBody = (
        <>
            {sortedData.map((row) => {
                const channelRef = parseTelegramPeerReference(row.name)

                return (
                    <tr
                        key={row.link_id}
                        className={clsx({
                            selected: selectedLinksSet.has(row.link_id),
                        })}
                    >
                        <td className="flex items-center justify-center">
                            {/* <div className='border-box'></div> */}
                            <Checkbox
                                checked={selectedLinksSet.has(row.link_id)}
                                onChange={() => onLinkSelect(row)}
                                className="row-checkbox"
                            />
                        </td>
                        <td className={clsx('name-cell')}>
                            <div className="name-cell__content">
                                {new Date().getTime() -
                                    new Date(row.creationDate).getTime() <=
                                    1 * 10 * 1000 && (
                                    <img
                                        src={NewIcon}
                                        alt="New"
                                        className="name-cell__new-icon"
                                    />
                                )}
                                {channelRef ? (
                                    <NiceChannelBox
                                        {...channelRef}
                                        title={row.title}
                                        channelId={activeChannel.channel_id}
                                        className={clsx(
                                            'name-cell__channel-box',
                                            {
                                                new:
                                                    new Date().getTime() -
                                                        new Date(
                                                            row.creationDate
                                                        ).getTime() <=
                                                    1 * 10 * 1000,
                                            }
                                        )}
                                        photoUrl={row.photoUrl}
                                        spanClassName="name-cell__channel-box__text"
                                        // rknStatus={row.rknStatus}
                                    />
                                ) : (
                                    <span className="name-cell__text">
                                        {row.name}
                                    </span>
                                )}
                            </div>
                        </td>
                        <td>
                            <CopyLink text={row.link} />
                        </td>
                        <td className={clsx('budget-cell')}>
                            {row.budget || '-'} <RubleIconInline />
                        </td>
                        <td>
                            {new Date(row.creationDate).toLocaleDateString(
                                'ru-RU'
                            )}
                        </td>
                        <td>{row.subscribers ?? '-'}</td>
                        <td>{row.reach ?? '-'}</td>
                        <td>{row.subscribed}</td>
                        <td>{row.CTR.toFixed(2)}%</td>
                        <td>
                            {row.CPM.toFixed(0)} <RubleIconInline />
                        </td>
                        <td>
                            {row.CPS.toFixed(0)} <RubleIconInline />
                        </td>
                        <td>{row.unsubscribed}</td>
                        <td>{row.unsubscriptionPercentage.toFixed(2)}%</td>
                        <td>{row.totalSubscribed}</td>
                        <td>
                            {row.total_CPS.toFixed(0)} <RubleIconInline />
                        </td>
                    </tr>
                )
            })}
        </>
    )
    return (
        <Table
            isLoading={false}
            thead={tableHead}
            tbody={tableBody}
            className="retention-table"
            baseTableClassName="retention-base-table"
        />
    )
}
